<template>
  <MessageNotificationDrawer :center="center" />
  <MessageNotificationDetail :center="center" @navigate="navigate" />
</template>
<script setup>
import { watch, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { messageApi } from '@/api/messages'
import { createMessageNavigationId, messageTargetRoute, waitForMessageNavigation } from '@/utils/messageNavigation'
import { registerMessageNavigation, readMessageNavigationReference, clearMessageNavigations } from '@/utils/messageNavigationContext'
import MessageNotificationDrawer from './MessageNotificationDrawer.vue'
import MessageNotificationDetail from './MessageNotificationDetail.vue'
import './MessageNotifications.css'
const props = defineProps({ center: { type: Object, required: true }, identityId: { type: [Number, String], default: null } })
const state = props.center.state
const router = useRouter(), route = useRoute()
let abort = new AbortController()
let restoredReference = null
let activeReturnRequestId = null
const hydrationRevision = state.viewRevision
function cancelNavigation() {
  abort.abort(); abort = new AbortController(); restoredReference = null; activeReturnRequestId = null
}
watch(() => props.identityId, () => { cancelNavigation(); clearMessageNavigations() }, { flush: 'sync' })
watch(() => state.viewRevision, cancelNavigation, { flush: 'sync' })
// 仅重建页面时接回返回引用；入口打开列表后，迟到的旧路由不能恢复旧详情。
watch(() => route.query?.messageReturn, requestId => {
  if (state.viewRevision !== hydrationRevision || state.open || state.detail || state.away) return
  const reference = readMessageNavigationReference(requestId, props.identityId)
  if (!reference) return
  activeReturnRequestId = requestId
  restoredReference = reference
  state.detailId = reference.messageId
  state.originRoute = reference.returnState.originRoute
  state.away = true
}, { immediate: true })
async function navigate() {
  if (!state.detail || state.actionLoading) return
  const id = state.detail.id, identity = props.identityId, view = state.viewRevision
  const current = () => identity === props.identityId && view === state.viewRevision && state.detail?.id === id
  const origin = route.fullPath
  state.actionLoading = true; state.targetError = ''
  try {
    const navigation = await messageApi.target(id, abort.signal)
    if (!current()) return
    const requestId = createMessageNavigationId()
    activeReturnRequestId = requestId
    const location = messageTargetRoute(navigation, requestId)
    if (navigation.target.type === 'ANALYSIS_RUN') location.query.notificationMessage = id
    const isFrame = ['SESSION_REQUEST', 'SESSION_MESSAGE', 'NOTEBOOK', 'RESEARCH_SHARE'].includes(navigation.target.type)
    if (isFrame) {
      registerMessageNavigation({ requestId, messageId: id, identityId: identity, navigation,
        returnState: { originRoute: origin, category: state.category, pendingOnly: state.pendingOnly, unreadOnly: state.unreadOnly,
          loadedIds: state.items.map(item => item.id), cursor: state.cursor, hasMore: state.hasMore,
          scrollTop: state.scrollTop, listedThroughId: state.listedThroughId, detailFilter: state.detailFilter } })
      const navigationAbort = new AbortController(), signal = abort.signal
      const cancel = () => navigationAbort.abort()
      signal.addEventListener('abort', cancel, { once: true })
      try {
        const ready = waitForMessageNavigation(requestId, identity, navigationAbort.signal)
        await Promise.all([router.push(location), ready])
      } finally { navigationAbort.abort(); signal.removeEventListener('abort', cancel) }
    } else await router.push(location)
    if (!current()) return
    state.originRoute = origin; state.away = true
  } catch (e) { if (current()) state.targetError = e.response?.data?.message || e.message || '跳转失败，请重试' }
  finally { if (current()) state.actionLoading = false }
}
async function returnToMessage() {
  const id = state.detailId, identity = props.identityId, view = state.viewRevision, requestId = activeReturnRequestId
  const reference = restoredReference
  const current = () => identity === props.identityId && view === state.viewRevision && requestId === activeReturnRequestId
  if (state.originRoute) await router.push(state.originRoute)
  if (!current()) return
  const restoring = !!reference
  if (restoring) {
    await props.center.restoreReturnState(reference.returnState)
    if (!current()) return
    restoredReference = null
  }
  state.away = false; state.open = true
  await Promise.all([restoring ? props.center.summary() : props.center.refreshLoaded(), id ? props.center.openDetail(id, true) : Promise.resolve()])
}
function returned(event) {
  if (String(event.detail?.identityId) !== String(props.identityId) || !activeReturnRequestId || event.detail?.requestId !== activeReturnRequestId) return
  event.preventDefault()
  const reference = readMessageNavigationReference(event.detail?.requestId, props.identityId)
  if (!reference && (!state.detailId || !state.originRoute)) { void props.center.open(); return }
  restoredReference = reference
  if (reference) {
    state.detailId = reference.messageId
    state.originRoute = reference.returnState.originRoute
  }
  void returnToMessage()
}
window.addEventListener('biointelos-message-return', returned)
onBeforeUnmount(() => { abort.abort(); window.removeEventListener('biointelos-message-return', returned) })
</script>
